import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Check, Sparkles, ArrowRight, ShoppingBag, Star, Clock, Zap, CreditCard } from "lucide-react";

// Ordered lookup so we can decide whether a chosen membership is an upgrade or downgrade.
const TIER_RANK = { essential: 1, progress: 2, performance: 3 };
const rankOf = (pkg) => TIER_RANK[(pkg?.id || pkg?.name || "").toLowerCase()] || 0;

export default function Packages() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [state, setState] = useState({ membership: null, purchases: [] });
  const [tab, setTab] = useState("memberships");
  const [portalLoading, setPortalLoading] = useState(false);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/checkout/portal", { return_url: window.location.href });
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open the billing portal");
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    Promise.all([
      api.get("/packages"),
      api.get("/me/membership").catch(() => ({ data: { membership: null, purchases: [] } })),
    ]).then(([p, m]) => { setItems(p.data); setState(m.data); });
  }, []);

  const memberships = useMemo(() => items.filter((p) => (p.type || "membership") === "membership"), [items]);
  const packages = useMemo(() => items.filter((p) => p.type === "package"), [items]);

  const activeMembership = state.membership;
  const activeMembershipRank = rankOf(activeMembership);
  const currentPackageName = activeMembership?.name;
  const purchases = state.purchases || [];
  const daysLeft = activeMembership?.expires_at ? Math.max(0, Math.round((new Date(activeMembership.expires_at) - new Date()) / 86400000)) : null;

  const membershipCta = (pkg) => {
    if (activeMembership && (pkg.id === activeMembership.package_id || pkg.name === currentPackageName)) {
      return { label: t("packages.current", "Current plan"), disabled: true, testid: `pkg-current-${pkg.id}`, variant: "ghost" };
    }
    if (activeMembership) {
      const r = rankOf(pkg);
      if (r > activeMembershipRank) return { label: t("packages.upgradeBtn", "Upgrade"), variant: "primary", icon: Zap, testid: `pkg-upgrade-${pkg.id}` };
      if (r < activeMembershipRank && r > 0) return { label: t("packages.downgradeBtn", "Downgrade"), variant: "ghost", testid: `pkg-downgrade-${pkg.id}` };
      return { label: t("packages.switchBtn", "Switch plan"), variant: "ghost", testid: `pkg-switch-${pkg.id}` };
    }
    return { label: t("packages.subscribe", "Subscribe"), variant: "primary", testid: `pkg-buy-${pkg.id}` };
  };

  return (
    <DashLayout>
      <div className="mb-8">
        <div className="fl-label text-[#f47a20]">{t("packages.eyebrow", "Membership & packages")}</div>
        <h1 className="fl-heading text-4xl md:text-5xl mt-2">{t("packages.h1", "Choose your plan")}</h1>
        <p className="mt-4 max-w-2xl" style={{ color: "var(--fl-muted)" }}>
          {t("packages.subtitle", "Ongoing memberships, structured rehabilitation packages or one-off sessions — every option is designed around your recovery goals.")}
        </p>
      </div>

      {/* ================ Your subscription block ================ */}
      <div className="mb-10" data-testid="your-subscription-block">
        <div className="fl-label mb-3 flex items-center gap-2">
          <Sparkles size={14} className="text-[#f47a20]" />
          {t("packages.yourSub", "Your subscription")}
        </div>

        {!activeMembership && purchases.length === 0 ? (
          <div className="fl-card p-6 flex items-start gap-4" data-testid="no-subscription-card">
            <ShoppingBag size={22} className="text-[#f47a20] shrink-0 mt-1" />
            <div className="flex-1">
              <div className="fl-heading text-xl">{t("packages.notSubbed", "You don't have an active plan yet")}</div>
              <div className="text-sm mt-1" style={{ color: "var(--fl-muted)" }}>
                {t("packages.notSubbedBody", "Choose a monthly membership for ongoing support, or a rehabilitation package for a focused programme.")}
              </div>
            </div>
            <div className="hidden md:flex gap-2">
              <button onClick={() => setTab("memberships")} className="fl-btn-anim fl-btn text-sm" data-testid="cta-see-memberships-empty">{t("packages.seeMemberships", "See memberships")}</button>
              <button onClick={() => setTab("packages")} className="fl-btn-anim fl-btn-ghost text-sm" data-testid="cta-see-packages-empty">{t("packages.orPackages", "Or packages")}</button>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {activeMembership && (
              <div className="fl-card p-6" style={{ borderColor: "var(--fl-orange)", borderWidth: 1.5 }} data-testid="active-membership-card">
                <div className="flex items-center gap-2">
                  <Star size={14} className="text-[#f47a20]" />
                  <div className="fl-label text-[#f47a20]">{t("packages.activeMembership", "Active membership")}</div>
                </div>
                <div className="fl-heading text-3xl mt-2">{activeMembership.name}</div>
                <div className="flex items-center gap-4 mt-3 text-sm" style={{ color: "var(--fl-muted)" }}>
                  {daysLeft !== null && (
                    <span className="flex items-center gap-1.5" data-testid="active-membership-days-left">
                      <Clock size={13} /> {daysLeft} {t("packages.daysLeft", { count: daysLeft, defaultValue: "days left" })}
                    </span>
                  )}
                  {activeMembership.started_at && (
                    <span>{t("packages.since", "since")} {new Date(activeMembership.started_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</span>
                  )}
                </div>
                {activeMembership.expires_at && (
                  <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>
                    {t("packages.renews", "Renews")} {new Date(activeMembership.expires_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </div>
                )}
                {activeMembershipRank > 0 && activeMembershipRank < 3 && (
                  <button className="fl-btn-anim fl-btn mt-4 text-sm" onClick={() => setTab("memberships")} data-testid="cta-upgrade-existing">
                    <Zap size={14} /> {t("packages.upgrade", "Upgrade")}
                  </button>
                )}
                <button
                  className="fl-btn-anim fl-btn-ghost mt-3 text-xs w-full justify-center"
                  onClick={openPortal}
                  disabled={portalLoading}
                  data-testid="cta-manage-subscription"
                >
                  <CreditCard size={12} />
                  {portalLoading ? t("packages.portalLoading", "Opening…") : t("packages.manage", "Manage subscription")}
                </button>
              </div>
            )}

            {purchases.length > 0 && (
              <div className="fl-card p-6" data-testid="active-purchases-card">
                <div className="fl-label">{t("packages.oneOffPackages", "One-off packages")}</div>
                <div className="fl-heading text-lg mt-2">
                  {purchases.length} {t("packages.packagesPurchased", { count: purchases.length, defaultValue: "purchased" })}
                </div>
                <div className="mt-3 space-y-2 text-sm" style={{ color: "var(--fl-muted)" }}>
                  {purchases.slice().reverse().slice(0, 4).map((p, i) => (
                    <div key={i} className="flex justify-between gap-2 border-b pb-2 last:border-none last:pb-0" style={{ borderColor: "var(--fl-border)" }} data-testid={`purchase-item-${i}`}>
                      <div className="min-w-0">
                        <div className="truncate" style={{ color: "var(--fl-text)" }}>{p.name}</div>
                        <div className="text-xs" style={{ color: "var(--fl-muted-2)" }}>
                          {p.purchased_at ? new Date(p.purchased_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : ""}
                        </div>
                      </div>
                      {p.amount != null && (
                        <div className="text-[#f47a20] shrink-0">£{p.amount}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ================ Tabs ================ */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 border-b" style={{ borderColor: "var(--fl-border)" }}>
        <div className="flex gap-2">
          {[["memberships", t("packages.tabMemberships", "Memberships")], ["packages", t("packages.tabPackages", "Packages")], ["services", t("packages.tabServices", "A la carte")]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`px-4 py-3 text-sm uppercase tracking-wider font-semibold ${tab === k ? "text-[#f47a20] border-b-2 border-[#f47a20] -mb-px" : "text-zinc-500 hover:text-zinc-300"}`}>
              {l}
            </button>
          ))}
        </div>
      </div>

      {/* ================ Memberships ================ */}
      {tab === "memberships" && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="memberships-grid">
          {memberships.map((p) => {
            const cta = membershipCta(p);
            const isCurrent = cta.disabled;
            return (
              <div
                key={p.id}
                className={`fl-card p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${p.popular ? "border-[#f47a20]" : ""} ${isCurrent ? "ring-2 ring-[#f47a20]/40" : ""}`}
                data-testid={`pkg-card-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  {p.popular && <div className="fl-label text-[#f47a20]">Most popular</div>}
                  {isCurrent && <div className="fl-label text-[#f47a20] ml-auto flex items-center gap-1"><Star size={12} /> Current</div>}
                </div>
                <div className="fl-heading text-2xl mt-2 break-words">{p.name}</div>
                <div className="mt-2 text-sm leading-relaxed italic" style={{ color: "var(--fl-muted)" }}>{p.tagline}</div>
                <div className="mt-4 flex items-baseline gap-1 flex-wrap">
                  <span className="fl-heading text-4xl md:text-5xl text-[#f47a20]">£{p.price}</span>
                  <span className="text-sm" style={{ color: "var(--fl-muted-2)" }}>/ month</span>
                </div>
                {p.billing?.weekly && (
                  <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>or £{p.billing.weekly.amount.toFixed(2)} weekly</div>
                )}
                <ul className="mt-6 space-y-2 flex-1 text-sm" style={{ color: "var(--fl-text-2)" }}>
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2"><Check size={14} className="text-[#f47a20] mt-1 shrink-0" /> {f}</li>
                  ))}
                </ul>
                {isCurrent ? (
                  <button
                    disabled
                    className="fl-btn-ghost mt-6 w-full justify-center opacity-70 cursor-not-allowed"
                    data-testid={cta.testid}
                  >
                    {cta.label}
                  </button>
                ) : (
                  <Link
                    to={`/checkout/summary?package=${p.id}&plan=membership`}
                    className={`fl-btn-anim mt-6 w-full justify-center ${cta.variant === "primary" ? "fl-btn" : "fl-btn-ghost"}`}
                    data-testid={cta.testid}
                  >
                    {cta.icon && <cta.icon size={14} />} {cta.label} <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ================ Packages ================ */}
      {tab === "packages" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="packages-grid">
          {packages.map((p) => <PackagePayCard key={p.id} pkg={p} />)}
        </div>
      )}

      {/* ================ A la carte ================ */}
      {tab === "services" && <ServicesTable />}

      {/* ================ Remote care ================ */}
      <div className="mt-12 fl-card p-6 md:p-8" data-testid="remote-care-block">
        <div className="fl-label text-[#f47a20] mb-3">Functional LAB remote care</div>
        <div className="fl-heading text-2xl md:text-3xl">Every package includes our hybrid care system.</div>
        <div className="grid md:grid-cols-4 gap-3 mt-6 text-sm" style={{ color: "var(--fl-text-2)" }}>
          {["Exercise adherence tracking", "Pain monitoring", "Wellness questionnaires", "RPE monitoring", "Progress tracking", "Tele-rehabilitation", "Secure digital exercise programmes", "Ongoing support between appointments"].map((f) => (
            <div key={f} className="flex items-start gap-2"><Check size={14} className="text-[#f47a20] mt-1 shrink-0" /> {f}</div>
          ))}
        </div>
      </div>

      {/* ================ Payment conditions ================ */}
      <div className="mt-4 fl-card p-5 md:p-6" data-testid="payment-conditions">
        <div className="fl-label text-[#f47a20] mb-2">Payment conditions</div>
        <ul className="text-xs md:text-sm space-y-1.5" style={{ color: "var(--fl-text-2)" }}>
          <li>· Pay in full is the best-value option and unlocks the saving shown on each package.</li>
          <li>· Weekly instalments offer greater payment flexibility over the programme.</li>
          <li>· Package instalments are payment plans for the complete package — not individual pay-as-you-go sessions.</li>
          <li>· Clients remain committed to completing payment for the package after starting it.</li>
          <li>· Monthly memberships operate as rolling memberships with cancellation subject to Functional LAB's terms and notice period.</li>
          <li>· All sessions should be used within the stated package duration unless an extension is agreed in advance.</li>
        </ul>
      </div>

      <div className="mt-4 text-xs" style={{ color: "var(--fl-muted-2)" }}>
        {t("packages.testCard")}
      </div>
    </DashLayout>
  );
}

/**
 * Package card with Weekly / Pay-in-full radio-tile selector — mirrors the
 * public /prices PackageCard so patients get the same purchase experience
 * inside the dashboard.
 */
function PackagePayCard({ pkg: p }) {
  const [plan, setPlan] = useState("full");
  const full = p.payment_options?.full?.total ?? p.price;
  const weekly = p.payment_options?.weekly;
  const weeklyTotal = weekly ? weekly.total : null;
  const save = weeklyTotal && weeklyTotal > full ? Math.round(weeklyTotal - full) : null;
  const href = `/checkout/summary?package=${p.id}&plan=${plan}`;

  return (
    <div
      className={`fl-card p-6 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${p.popular ? "border-[#f47a20]" : ""}`}
      data-testid={`pkg-card-${p.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="fl-label text-[#f47a20]">{p.popular ? "Most popular" : "Package"}</div>
          <div className="fl-heading text-2xl mt-2">{p.name}</div>
          {p.duration && <div className="text-xs mt-1" style={{ color: "var(--fl-muted-2)" }}>Recommended duration: <b>{p.duration}</b></div>}
        </div>
        <div className="text-right shrink-0">
          <div className="fl-heading text-4xl text-[#f47a20]">£{full}</div>
          <div className="text-[10px]" style={{ color: "var(--fl-muted-2)" }}>total · pay in full</div>
        </div>
      </div>
      <div className="text-sm mt-3 italic" style={{ color: "var(--fl-muted)" }}>{p.tagline}</div>

      {weekly && (
        <div className="mt-4 grid grid-cols-2 gap-2" role="radiogroup" aria-label="Payment plan" data-testid={`pkg-plan-selector-${p.id}`}>
          <button
            type="button"
            onClick={() => setPlan("weekly")}
            role="radio"
            aria-checked={plan === "weekly"}
            className={`text-left p-3 rounded-md border transition-all duration-200 ${
              plan === "weekly"
                ? "border-[#f47a20] bg-[color:color-mix(in_oklab,#f47a20_10%,transparent)] scale-[1.02]"
                : "border-white/10 hover:border-white/25 hover:-translate-y-0.5"
            }`}
            data-testid={`pkg-plan-weekly-${p.id}`}
          >
            <div className={`fl-label ${plan === "weekly" ? "text-[#f47a20]" : ""}`}>Weekly instalments</div>
            <div className="text-sm mt-1" style={{ color: "var(--fl-text)" }}>{weekly.installments} × £{weekly.amount.toFixed(2)}</div>
            <div className="text-[10px] mt-0.5" style={{ color: "var(--fl-muted-2)" }}>Total £{weekly.total.toFixed(2)}</div>
          </button>
          <button
            type="button"
            onClick={() => setPlan("full")}
            role="radio"
            aria-checked={plan === "full"}
            className={`text-left p-3 rounded-md border transition-all duration-200 ${
              plan === "full"
                ? "border-[#f47a20] bg-[color:color-mix(in_oklab,#f47a20_10%,transparent)] scale-[1.02]"
                : "border-white/10 hover:border-white/25 hover:-translate-y-0.5"
            }`}
            data-testid={`pkg-plan-full-${p.id}`}
          >
            <div className={`fl-label ${plan === "full" ? "text-[#f47a20]" : ""}`}>Pay in full</div>
            <div className="text-sm mt-1" style={{ color: "var(--fl-text)" }}>£{full}</div>
            {save != null && <div className="text-[10px] text-emerald-400 mt-0.5 font-semibold">Save £{save}</div>}
          </button>
        </div>
      )}

      <div className="fl-label mt-5 mb-2">What's included</div>
      <ul className="space-y-2 text-sm flex-1" style={{ color: "var(--fl-text-2)" }} data-testid={`pkg-features-${p.id}`}>
        {(p.features || []).map((f) => (
          <li key={f} className="flex items-start gap-2"><Check size={14} className="text-[#f47a20] mt-1 shrink-0" /> <span>{f}</span></li>
        ))}
      </ul>

      {p.notice && (
        <div className="mt-4 text-[11px] p-2.5 rounded-md" style={{ background: "color-mix(in oklab, #F59E0B 6%, transparent)", border: "1px solid #F59E0B44", color: "#F59E0B" }}>
          {p.notice}
        </div>
      )}

      <div className="mt-5 flex flex-col sm:flex-row gap-2">
        <Link to={href} className="fl-btn-anim fl-btn text-xs flex-1 justify-center group" data-testid={`pkg-buy-${p.id}`}>
          Choose & pay <ArrowRight size={12} className="transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
        <Link to="/app/messages" className="fl-btn-anim fl-btn-ghost text-xs flex-1 justify-center" data-testid={`pkg-speak-${p.id}`}>
          Speak to a clinician
        </Link>
      </div>
    </div>
  );
}

const SERVICES = [
  ["Initial Physiotherapy Assessment (60 min)", "£95"],
  ["Physiotherapy Follow-Up (45 min)", "£75"],
  ["Short Follow-Up (30 min)", "£55"],
  ["Five Follow-Up Sessions (5 × 45 min)", "£350"],
  ["Strength & Conditioning (60 min)", "£100"],
  ["Sports massage / recovery (60 min)", "£60"],
  ["Home Visits", "Individually quoted"],
];

function ServicesTable() {
  return (
    <div className="fl-card overflow-hidden" data-testid="services-table">
      <div className="grid grid-cols-12 gap-4 p-4 border-b fl-label" style={{ borderColor: "var(--fl-border)" }}>
        <div className="col-span-9">Service</div>
        <div className="col-span-3 text-right">Price</div>
      </div>
      {SERVICES.map(([name, price]) => (
        <div key={name} className="grid grid-cols-12 gap-4 p-4 border-b text-sm" style={{ borderColor: "var(--fl-border)" }} data-testid={`svc-${name.split(" ")[0].toLowerCase()}`}>
          <div className="col-span-9" style={{ color: "var(--fl-text-2)" }}>{name}</div>
          <div className="col-span-3 text-right fl-heading text-[#f47a20]">{price}</div>
        </div>
      ))}
      <div className="p-4 text-xs" style={{ color: "var(--fl-muted-2)" }}>
        Members receive 10–20% discount on all additional services depending on their tier.
      </div>
    </div>
  );
}
